/**
 * The application shell: top bar, left sidebar, page header.
 *
 * ACTIVE NAV IS DECIDED HERE, ONCE. Every nav item declares its `path`; this component reads the
 * current route and decides which single item is active (BlueHomer §2.4.5.1). No page computes
 * `active` for itself — a page adding a screen only declares label, icon and path.
 *
 * Every module also has an Overview whose path equals the route its top-nav link points to, so a
 * module can never load with zero nav items selected (§2.4 rule 5.3).
 */

import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'

import { useApp } from '../../contexts/app_context'
import { useAuth } from '../../contexts/auth_context'

interface NavItem {
  label: string
  icon: string
  path: string
  end?: boolean
}

interface NavGroup {
  group: string
  items: NavItem[]
}

interface ModuleDef {
  key: string
  label: string
  icon: string
  description: string
  root: string
  groups: NavGroup[]
}

export const MODULES: ModuleDef[] = [
  {
    key: 'home',
    label: 'Home',
    icon: 'cottage',
    description: 'What needs your attention today — follow-ups, waiting relationships, priorities.',
    root: '/home',
    groups: [
      {
        group: 'Home',
        items: [
          { label: 'Daily Workspace', icon: 'today', path: '/home', end: true },
          { label: 'My Relationships', icon: 'diversity_3', path: '/home/my-relationships' },
        ],
      },
      {
        group: 'Saved Views',
        items: [{ label: 'All Saved Views', icon: 'bookmarks', path: '/data/saved-views' }],
      },
    ],
  },
  {
    key: 'relationships',
    label: 'Relationships',
    icon: 'diversity_3',
    description: 'People, organizations, leads and opportunities, and where each one stands.',
    root: '/relationships',
    groups: [
      {
        group: 'Relationships',
        items: [
          { label: 'Overview', icon: 'dashboard', path: '/relationships', end: true },
          { label: 'People', icon: 'person', path: '/relationships/people' },
          { label: 'Organizations', icon: 'business', path: '/relationships/organizations' },
          { label: 'Leads', icon: 'filter_alt', path: '/relationships/leads' },
          { label: 'Opportunities', icon: 'trending_up', path: '/relationships/opportunities' },
          { label: 'Pipeline', icon: 'view_kanban', path: '/relationships/pipeline' },
        ],
      },
    ],
  },
  {
    key: 'talent',
    label: 'Talent',
    icon: 'badge',
    description: 'Candidates, consultants, resumes and the talent pipeline.',
    root: '/talent',
    groups: [
      {
        group: 'Talent',
        items: [
          { label: 'Overview', icon: 'dashboard', path: '/talent', end: true },
          { label: 'Candidates', icon: 'badge', path: '/talent/candidates' },
          { label: 'Resume Search', icon: 'manage_search', path: '/talent/resume-search' },
        ],
      },
    ],
  },
  {
    key: 'engagement',
    label: 'Engagement',
    icon: 'forum',
    description: 'Activities, follow-ups, meetings, campaigns and lists.',
    root: '/engagement',
    groups: [
      {
        group: 'Engagement',
        items: [
          { label: 'Overview', icon: 'dashboard', path: '/engagement', end: true },
          { label: 'Activities', icon: 'history', path: '/engagement/activities' },
          { label: 'Follow-Ups', icon: 'event_upcoming', path: '/engagement/follow-ups' },
          { label: 'Campaigns', icon: 'campaign', path: '/engagement/campaigns' },
        ],
      },
    ],
  },
  {
    key: 'data',
    label: 'Data',
    icon: 'database',
    description: 'Import contacts, search everything, keep the views you use every day.',
    root: '/data',
    groups: [
      {
        group: 'Data',
        items: [
          { label: 'Overview', icon: 'dashboard', path: '/data', end: true },
          { label: 'Search', icon: 'manage_search', path: '/data/search' },
          { label: 'Saved Views', icon: 'bookmark', path: '/data/saved-views' },
          { label: 'Imports', icon: 'upload_file', path: '/data/imports' },
        ],
      },
    ],
  },
  {
    key: 'admin',
    label: 'Administration',
    icon: 'settings',
    description: 'Reference values, tags and integrations — all configuration, no code change.',
    root: '/admin',
    groups: [
      {
        group: 'Administration',
        items: [
          { label: 'Overview', icon: 'dashboard', path: '/admin', end: true },
          { label: 'Categories & Statuses', icon: 'category', path: '/admin/reference' },
          { label: 'Tags', icon: 'sell', path: '/admin/tags' },
          { label: 'Settings', icon: 'tune', path: '/admin/settings' },
        ],
      },
    ],
  },
]

function moduleForPath(pathname: string): ModuleDef {
  //  Longest matching root wins, so /relationships/people picks Relationships rather than Home.
  const match = [...MODULES]
    .sort((a, b) => b.root.length - a.root.length)
    .find((m) => pathname === m.root || pathname.startsWith(`${m.root}/`))
  return match ?? MODULES[0]
}

/**
 * Shown for as long as reference data is missing.
 *
 * NOT ONLY A TOAST. A toast can be dismissed, and once it is, the application looks normal while
 * every status dropdown is still empty — so the next person to hit it reports the dropdowns rather
 * than the load failure. This stays until a retry succeeds.
 */
function ReferenceBanner() {
  const { referenceError, reload } = useApp()
  const [retrying, setRetrying] = useState(false)
  if (!referenceError) return null
  return (
    <div className="banner warn" style={{ margin: '0 0 10px' }}>
      <span className="material-symbols-outlined">rule</span>
      <div>
        <b>Statuses and types are unavailable, so every dropdown is empty.</b>
        <div style={{ marginTop: 4 }}>{referenceError}</div>
        <button
          className="btn secondary sm"
          type="button"
          style={{ marginTop: 8 }}
          disabled={retrying}
          onClick={async () => {
            setRetrying(true)
            try {
              await reload()
            } finally {
              setRetrying(false)
            }
          }}
        >
          <span className="material-symbols-outlined">refresh</span>
          {retrying ? 'Retrying…' : 'Try again'}
        </button>
      </div>
    </div>
  )
}

export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const { config, userId } = useApp()
  const { session, signOut } = useAuth()
  const active = moduleForPath(pathname)

  return (
    <div className="app">
      {/* Left Sidebar Navigation */}
      <aside className="sidebar">
        <div className="tf-brand-block">
          <NavLink className="tf-brand-row" to="/home" style={{ textDecoration: 'none' }}>
            <img className="tf-brand-logo" src="/img/tidalfin-mark-light.svg" alt="" width={38} height={38} />
            <span>
              <span className="tf-brand-name">{config?.applicationName ?? 'Tidalfin'}</span>
              <span className="tf-brand-tag">{config?.applicationTagline ?? ''}</span>
            </span>
          </NavLink>
          <div className="tf-div" />
          <div className="tf-client">
            <div className="tf-client-icon" aria-hidden="true">
              <span className="material-symbols-outlined">business</span>
            </div>
            <div className="tf-client-name">{config?.tenantName ?? '—'}</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <span className="tf-id">{config?.clientAccountId ?? ''}</span>
          </div>
        </div>

        <div className="mod-banner">
          <div className="mod-kicker">Application Module</div>
          <div className="mod-title">
            <span className="material-symbols-outlined" aria-hidden="true">
              {active.icon}
            </span>
            {active.label}
          </div>
          <p className="mod-desc">{active.description}</p>
        </div>

        <nav className="nav-tree" aria-label={`${active.label} navigation`}>
          {active.groups.map((group) => (
            <div className="nav-group" key={group.group}>
              <div className="nav-group-title">{group.group}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  /* THE centralization: NavLink resolves `active` from the route. No page ever
                     passes an `active` flag, so adding a screen cannot get highlighting wrong. */
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="right-col">
        {/* Top Navigation Bar */}
        <header className="topbar">
          <nav className="domain-nav" aria-label="Modules">
            {MODULES.map((m) => (
              <NavLink key={m.key} to={m.root} className={m.key === active.key ? 'active' : ''}>
                {m.label}
              </NavLink>
            ))}
          </nav>

          <form
            className="global-search"
            onSubmit={(e) => {
              e.preventDefault()
              const value = new FormData(e.currentTarget).get('q')
              if (value) window.location.assign(`/data/search?q=${encodeURIComponent(String(value))}`)
            }}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              search
            </span>
            <input type="search" name="q" placeholder="Search people, organizations, notes…" aria-label="Global search" />
          </form>

          <div className="utils">
            <NavLink className="utility-btn" to="/engagement/follow-ups" aria-label="Follow-ups" title="Follow-ups">
              <span className="material-symbols-outlined" aria-hidden="true">
                event_upcoming
              </span>
            </NavLink>
            <NavLink className="profile-pill" to="/admin/settings">
              <span className="avatar" aria-hidden="true">
                {(session.displayName || userId).slice(0, 2).toUpperCase()}
              </span>
              {session.displayName || userId}
            </NavLink>
            {/* Signing out has to be reachable from every screen, not filed under Settings: the
                usual reason to want it is being on the wrong account, and hunting for it while
                logged in as the wrong person is the worst moment to go looking. */}
            <button
              type="button"
              className="utility-btn"
              onClick={() => void signOut()}
              aria-label="Sign out"
              title={`Sign out${session.email ? ` (${session.email})` : ''}`}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                logout
              </span>
            </button>
          </div>
        </header>

        <div className="content-area">
          <div className="page-col"><ReferenceBanner />
          {children}</div>
        </div>
      </div>
    </div>
  )
}

/** Page Header Section — crumb, title, description, actions. */
export function PageHeader({
  crumb,
  title,
  description,
  actions,
}: {
  crumb: ReactNode
  title: string
  description?: string
  actions?: ReactNode
}) {
  return (
    <section className="page-head">
      <div className="ph-top">
        <div>
          <div className="crumb">{crumb}</div>
          <h1>{title}</h1>
          {description && <p>{description}</p>}
        </div>
        {actions && <div className="head-actions">{actions}</div>}
      </div>
    </section>
  )
}
